// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web, étape 3) — éléments communs aux écrans des campagnes : état, bandeaux (simulation,
// démonstration, retenues en simulation), conditions du règlement, récapitulatif calculé par le serveur, quantités
// en unités de la campagne, export CSV tracé de l'état. Composants définis hors de tout parent (règle 1).
// Aucun libellé de campagne, aucune unité, aucun montant n'est écrit ici : tout vient du serveur.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Settings2 } from 'lucide-react';
import { Badge, Bouton, BandeauInfo } from '../../design/composants';
import { toArray, libelle } from './api';
import { ErreurServeur, ouvrirBlob } from './commun';
import { francs, dateLongue, moisLong } from './apiSocial';
import { MarqueDemo, Infos } from './communSocial';
import {
  useCampagnes, quantiteUnites, ETATS_CAMPAGNE, REGLES_ATTRIBUTION, PLAFONNEMENTS, MODES_QUOTITE, MODES_REMBOURSEMENT,
} from './apiCampagnes';

export const LIEN_CENTRE = '/parametres/dispositifs';

/**
 * Adresse du Centre de configuration à la bonne portée (DM-CONFIGUI-001) : dispositif, organisation (facultative),
 * sous-portée CAMPAGNE ou TYPE_AIDE et son objet. Sans dispositif connu : adresse générique.
 */
export function lienCentre({ organisation, dispositif, portee, objet } = {}) {
  const q = new URLSearchParams();
  if (organisation) q.set('organisation', organisation);
  if (dispositif) q.set('dispositif', dispositif);
  if (dispositif && portee && objet) { q.set('portee', portee); q.set('objet', objet); }
  const s = q.toString();
  return s ? `${LIEN_CENTRE}?${s}` : LIEN_CENTRE;
}
/** Portée d'une campagne pour lienCentre. */
export const porteeCampagne = (c) => (c ? { organisation: c.organisation_id, dispositif: c.dispositif_id, portee: 'CAMPAGNE', objet: c.id } : {});

/** État d'une campagne : libellé du serveur ({ code, libelle }), ton selon le code. */
export function EtatCampagne({ etat }) {
  const code = etat && typeof etat === 'object' ? etat.code : etat;
  const [ton, lib] = ETATS_CAMPAGNE[code] || ['neutre', code || ''];
  return <Badge ton={ton} point>{(etat && etat.libelle) || lib}</Badge>;
}
export const codeEtat = (c) => (c && c.etat && typeof c.etat === 'object' ? c.etat.code : c?.etat);

/** Badges d'une campagne : simulation, démonstration. */
export const MarquesCampagne = ({ campagne }) => (
  <>
    {campagne.simulation && <Badge ton="info" title="Campagne de simulation : les créances qui en naissent ne sont jamais transmises à la Paie">Simulation</Badge>}{' '}
    <MarqueDemo actif={campagne.donnees_demo} />
  </>
);

/** Bandeaux d'une campagne de simulation et de données de démonstration. */
export function BandeauxCampagne({ campagne }) {
  return (
    <>
      {campagne.simulation && (
        <BandeauInfo ton="info" titre="Campagne de simulation">
          Toute la chaîne se déroule (inscriptions, achats, remises), mais les créances qui en naissent sont des créances de simulation : jamais transmises à la Paie, aucun paiement réel.
        </BandeauInfo>
      )}
      {campagne.donnees_demo && (
        <BandeauInfo ton="alerte" titre="[DÉMO] Données de démonstration">
          Campagne de recette : les montants, les personnes et les pièces sont fictifs.
        </BandeauInfo>
      )}
    </>
  );
}

/** Lien vers le Centre de configuration (le règlement s'y prépare, s'y simule et s'y valide). */
export const LienCentre = ({ children = 'Centre de configuration', portee = {} }) => (
  <Link className="rh-lien" to={lienCentre(portee)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
    <Settings2 size={15} aria-hidden="true" />{children}
  </Link>
);

/**
 * Conditions du règlement d'une campagne (valeurs normalisées par le serveur) : prix unitaire, aide par unité, unités
 * aidées par salarié, mensualités, première période, dates, quantités, règles. `campagne` fournit les unités.
 */
export function ConditionsCampagne({ campagne, conditions }) {
  const c = conditions;
  if (!c) return null;
  const u = campagne.unite_singulier;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
        {[
          ['Prix unitaire', `${francs(c.prix_unitaire)} par ${u}`],
          ['Aide par unité', francs(c.subvention_unitaire)],
          ['Unités aidées par salarié', quantiteUnites(campagne, c.unites_subventionnables)],
          ['Remboursement', `${c.nb_mensualites} mois`],
          ['Premier mois de remboursement', moisLong(c.premiere_periode)],
        ].map(([l, v]) => (
          <div key={l} className="rh-carte" style={{ margin: 0, padding: '10px 12px' }}>
            <div style={{ fontSize: 12, color: 'var(--sg-texte-3)' }}>{l}</div>
            <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums', marginTop: 2, overflowWrap: 'anywhere' }}>{v}</div>
          </div>
        ))}
      </div>
      <Infos lignes={[
        ['Inscriptions', c.date_ouverture ? `du ${dateLongue(c.date_ouverture)} au ${dateLongue(c.date_cloture)}` : null],
        ['Désistement possible jusqu\'au', dateLongue(c.date_limite_desistement)],
        ['Retrait jusqu\'au (indicatif)', c.date_limite_retrait ? dateLongue(c.date_limite_retrait) : null],
        ['Quantité disponible', quantiteUnites(campagne, c.quantite_disponible)],
        ['Au plus par salarié', quantiteUnites(campagne, c.quantite_max_salarie)],
        ['Inscriptions', libelle(PLAFONNEMENTS, c.plafonnement)],
        ['Règle d\'attribution', libelle(REGLES_ATTRIBUTION, c.regle_attribution)],
        ['Mode de remboursement', libelle(MODES_REMBOURSEMENT, c.mode_remboursement)],
        ['Part du salaire retenue', libelle(MODES_QUOTITE, c.quotite_mode)],
        ['Arrondi des montants mensuels', c.unite_arrondi ? francs(c.unite_arrondi) : null],
        ['Ancienneté minimale', c.anciennete_min_mois ? `${c.anciennete_min_mois} mois` : null],
        ['Version du règlement', c.reglement_version ? `version ${c.reglement_version}` : null],
      ]} />
      {c.texte_engagement && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--sg-texte-3)', marginBottom: 2 }}>Texte d'engagement présenté au salarié</div>
          <blockquote style={{ margin: 0, padding: '8px 12px', borderLeft: '3px solid var(--sg-bord-fort)', fontSize: 14, color: 'var(--sg-texte-2)', overflowWrap: 'anywhere' }}>{c.texte_engagement}</blockquote>
        </div>
      )}
    </>
  );
}

/** Récapitulatif calculé par le serveur (lignes, phrase, avertissements) — jamais recalculé ici. */
export function RecapitulatifServeur({ recap }) {
  if (!recap) return null;
  const r = recap.recapitulatif || recap;
  return (
    <div className="rh-carte" style={{ margin: '0 0 12px', padding: '10px 14px', background: 'var(--sg-fond)' }}>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '4px 12px', fontSize: 14 }}>
        {toArray(r.lignes).map((l) => (
          <div key={l.libelle} style={{ display: 'contents' }}>
            <dt style={{ color: 'var(--sg-texte-2)' }}>{l.libelle}</dt>
            <dd style={{ margin: 0, fontWeight: l.fort ? 600 : 400, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{l.valeur}</dd>
          </div>
        ))}
      </dl>
      {r.phrase && <p style={{ margin: '8px 0 0', fontWeight: 600 }}>{r.phrase}</p>}
      {toArray(r.avertissements).map((a) => <p key={a} className="rh-form-aide" style={{ margin: '6px 0 0', color: 'var(--sg-alerte, inherit)' }}>{a}</p>)}
    </div>
  );
}

/** Erreur « règle absente » : la liste du serveur (« CODE : libellé ») est rendue par libellés seulement. */
export function ReglesAbsentes({ erreur, campagne = null }) {
  if (!erreur) return null;
  const lib = (x) => String(x).replace(/^[A-Z0-9_]+ : /, '');
  return (
    <BandeauInfo ton="alerte" titre="Règlement de la campagne incomplet" action={<LienCentre portee={porteeCampagne(campagne)} />}>
      {erreur.message}
      {erreur.erreurs.length > 0 && <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>{erreur.erreurs.map((x) => <li key={String(x)}>{lib(x)}</li>)}</ul>}
    </BandeauInfo>
  );
}

/** Bouton d'export CSV de l'état (une ligne par inscription, export tracé par le serveur). */
export function BoutonExportEtat({ campagne, libelleBouton = 'Exporter (CSV)' }) {
  const api = useCampagnes();
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const exporter = async () => {
    setEnCours(true); setErreur(null);
    try {
      const f = await api.etatCsv(campagne.id);
      ouvrirBlob(f, `etat-campagne-${campagne.numero || campagne.id}.csv`);
    } catch (e) { setErreur(e.normalisee || { statut: 0, code: null, message: 'Export impossible.', erreurs: [] }); }
    setEnCours(false);
  };
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
      <Bouton Icone={Download} disabled={enCours} onClick={exporter} title="Export nominatif tracé dans le journal d'audit">{libelleBouton}</Bouton>
      {erreur && <ErreurServeur erreur={erreur} />}
    </span>
  );
}

/** Somme des quantités des inscriptions actives à partir des comptes du détail { STATUT: { nombre, quantite } }. */
export function unitesInscrites(comptes) {
  const actifs = ['INSCRITE', 'VERIFIEE', 'ATTRIBUEE', 'PARTIELLEMENT_REMISE', 'REMISE'];
  return actifs.reduce((s, k) => s + Number((comptes && comptes[k] && comptes[k].quantite) || 0), 0);
}
export function nombreInscrits(comptes) {
  const actifs = ['INSCRITE', 'VERIFIEE', 'ATTRIBUEE', 'PARTIELLEMENT_REMISE', 'REMISE'];
  return actifs.reduce((s, k) => s + Number((comptes && comptes[k] && comptes[k].nombre) || 0), 0);
}

/** Champ numérique entier (quantité) : renvoie l'entier ou null. */
export const lireEntier = (s) => (/^\d{1,9}$/.test(String(s ?? '').trim()) ? Number(String(s).trim()) : null);
